/** Color maths shared by the heat scale and option chips. */

function channel(v: number): number {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

export function luminance(hex: string): number {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255);
}

export function contrastRatio(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** The system's two inks (design.md §1): the dark ink on light fills, white on dark fills. */
export const DARK_INK = '#0a2e33';
export const LIGHT_INK = '#ffffff';

/**
 * Whichever of two inks clears AA on a fill. Used wherever a color arrives as DATA
 * (an option row, a brand profile from /api/meta) and its label still has to be readable.
 */
export function readableInk(bg: string, darkInk = DARK_INK, lightInk = LIGHT_INK): string {
  return contrastRatio(bg, darkInk) >= contrastRatio(bg, lightInk) ? darkInk : lightInk;
}

/**
 * `#rgb` or `#rrggbb` only (A20). 4- and 8-digit forms (`#rgba`, `#rrggbbaa`) used to pass this
 * check; `luminance`/`rgb` below don't know about an alpha channel, so they measured the alpha
 * nibble as part of blue and `pillColors` painted a fill that was see-through in the browser
 * but opaque by its own math — "readable by construction" silently failed. Anything that isn't
 * 3 or 6 hex digits after the `#` (including non-hex strings like `"red"`) falls through to the
 * caller's neutral role instead — no throw, no console noise, just a plain fallback.
 */
export function isHex(value: string | null | undefined): value is string {
  return typeof value === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value);
}

/** Flat color at a given alpha over the current surface — used for soft option chips. */
export function tint(hex: string, alpha: number): string {
  return `color-mix(in srgb, ${hex} ${Math.round(alpha * 100)}%, transparent)`;
}

/** One channel triple from a #rrggbb string. */
function rgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h.slice(0, 6);
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function hex2(n: number): string {
  return Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
}

/**
 * `ratio` of `a` over `b`, flattened to an opaque hex. Used to derive the tinted surfaces and
 * hover fills a brand profile does not supply, so the result can be AA-checked like any other
 * color — a `color-mix()` string cannot be.
 */
export function mixHex(a: string, b: string, ratio: number): string {
  const [ar, ag, ab] = rgb(a);
  const [br, bg, bb] = rgb(b);
  const t = Math.max(0, Math.min(1, ratio));
  return `#${hex2(ar * t + br * (1 - t))}${hex2(ag * t + bg * (1 - t))}${hex2(ab * t + bb * (1 - t))}`;
}

/** WCAG AA for normal text — the floor for every pill, chip and label (design.functional §8). */
export const AA_TEXT = 4.5;

/**
 * Moves `color` toward `toward` in 5% steps until it reaches `min` against `against`.
 * Returns the first color that passes, or `toward` itself (which the caller chose because it
 * passes). Used to derive a text-safe variant of a fill color without inventing a hue.
 */
export function ensureContrast(color: string, against: string, min: number, toward: string): string {
  for (let step = 0; step <= 20; step++) {
    const c = step === 0 ? color : mixHex(toward, color, step / 20);
    if (contrastRatio(c, against) >= min) return c;
  }
  return toward;
}

export interface PillColors {
  /** The fill actually painted — the option's own color, deepened or lifted only if no ink clears AA on it. */
  bg: string;
  /** The label color, derived from the fill, never chosen per screen. */
  fg: string;
}

/**
 * Text on any colored fill derives from the fill (design.md §1). Both inks are tried against
 * the fill AS STORED (`readableInk`) before anything is altered — a fill like `#d07e00`
 * (luminance 0.2833, just under the light-ink side of a naive threshold) already clears AA with
 * the dark ink at 4.60:1 and must be painted exactly as the option row stored it, not deepened
 * toward black on top of an ink choice that never needed one. Only a genuine mid-tone that
 * clears AA with NEITHER ink (the brand's Dusk is 3.94 with dark ink, 3.67 with white) is
 * deepened toward black — or lifted toward white — just far enough to pass, so a pill is
 * readable by construction in both modes whatever color an option row carries.
 */
export function pillColors(fill: string, darkInk = DARK_INK, lightInk = LIGHT_INK): PillColors {
  const fg = readableInk(fill, darkInk, lightInk);
  if (contrastRatio(fg, fill) >= AA_TEXT) return { bg: fill, fg };
  const toward = fg === lightInk ? '#000000' : '#ffffff';
  for (let step = 1; step <= 20; step++) {
    const bg = mixHex(toward, fill, step / 20);
    if (contrastRatio(fg, bg) >= AA_TEXT) return { bg, fg };
  }
  return { bg: toward, fg };
}
